import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RotateCw, SlidersHorizontal } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const METRICS = [["open", "open", ""], ["unworked", "unworked", "text-amber-500"], ["working", "working", "text-emerald-500"], ["pending_approval", "approval", "text-purple-500"], ["closed_today", "closed today", ""]];

function Group({ label, color, data, testId }) {
  return (
    <div className="flex shrink-0 items-center gap-3 border-r border-border px-4 last:border-r-0" data-testid={testId}>
      <span className="flex items-center gap-1.5 eyebrow text-foreground"><span className="h-2 w-2" style={{ background: color || "hsl(var(--primary))" }} />{label}</span>
      {METRICS.map(([k, l, tone]) => (
        <span key={k} className="flex items-baseline gap-1" data-testid={`${testId}-${k}`}>
          <span className={cn("mono text-sm font-semibold tabular-nums", data?.[k] ? tone : "text-muted-foreground")}>{data?.[k] ?? "–"}</span>
          <span className="eyebrow">{l}</span>
        </span>
      ))}
    </div>
  );
}

export default function MetricsStrip() {
  const qc = useQueryClient();
  const { can, readOnly } = useAuth();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/overview").then((r) => r.data), refetchInterval: 15000 });
  const featured = data?.featured_channel_ids || [];
  const all = data?.all_channels || [];
  const save = async (ids, msg) => {
    try {
      await api.patch("/settings", { featured_channel_ids: ids });
      qc.invalidateQueries({ queryKey: ["overview"] });
      if (msg) toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (id, on) => {
    if (on && featured.length >= 3) return toast.info("Up to 3 channels can be shown. Untick one first.");
    save(on ? [...featured, id] : featured.filter((x) => x !== id));
  };
  const rotate = () => {
    if (all.length <= 3) return toast.info("All channels are already shown");
    const idx = all.findIndex((c) => c.id === featured[featured.length - 1]);
    const next = [...Array(3).keys()].map((i) => all[(idx + 1 + i) % all.length].id);
    save(next, "Rotated to the next channels");
  };

  return (
    <div className="flex h-10 shrink-0 items-center overflow-x-auto border-b border-border bg-card/60" data-testid="metrics-strip">
      <Group label="All" data={data?.totals} testId="metrics-all" />
      {data?.channels?.map((c) => <Group key={c.id} label={c.name} color={c.color} data={c} testId={`metrics-channel-${c.id}`} />)}
      {can("settings.manage") && !readOnly && (
        <div className="ml-auto flex shrink-0 items-center gap-1 px-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={rotate} title="Rotate to next channels" data-testid="metrics-rotate-btn"><RotateCw className="h-3.5 w-3.5" /></Button>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" title="Choose channels" data-testid="metrics-pick-btn"><SlidersHorizontal className="h-3.5 w-3.5" /></Button></PopoverTrigger>
            <PopoverContent align="end" className="w-64 bg-popover p-2" data-testid="metrics-pick-popover">
              <div className="eyebrow mb-1.5 px-1">Channels on the strip (max 3)</div>
              <div className="max-h-64 divide-y divide-border overflow-auto border border-border">
                {all.map((c) => (
                  <label key={c.id} className="row-hover flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs">
                    <Checkbox checked={featured.includes(c.id)} onCheckedChange={(v) => toggle(c.id, !!v)} data-testid={`metrics-pick-${c.id}`} />
                    <span className="h-2 w-2" style={{ background: c.color }} />{c.name}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
